/**
 * A product's KIND is the title of the vitrine section it lands in (CAT-016),
 * and three surfaces have to answer the same two questions about it: what do we
 * call this kind, and in what order do the sections run?
 *
 * Both were re-derived per screen before this file, and the two halves of the
 * answer disagreed the moment a curator typed a kind of their own: the guest
 * menu grouped by `kind` but titled its sections from two hardcoded copy keys,
 * so a "Boissons" section rendered under the heading "Extras" - or under the
 * raw key `menu.sectionBoissons`, depending on which screen you were on.
 *
 * A kind is a RECORD now - a key, an English name, a French one and a position
 * in the running order - and this module is the one place those three facts are
 * read. There is no second class of kind: `MENU` and `EXTRA` shipped with this
 * codebase and are otherwise ordinary rows, editable, removable and reorderable
 * like every other (founder, 2026-08-27). A key with no record yet reads as
 * itself, which is what every kind did before the registry existed.
 */

/**
 * The words a curator authored for one kind, in both languages the platform
 * publishes (NFR-006). `en` is the authored default and the fallback; `fr` may
 * be blank while the French copy is still being written, exactly as a product's
 * own French name may be - a blank falls back rather than rendering an empty
 * heading (CAT-002).
 */
export interface ProductTypeLabels {
  readonly en: string;
  readonly fr: string;
}

/** Every authored kind, by the key the items themselves carry. */
export type ProductTypeCatalogue = Readonly<Record<string, ProductTypeLabels>>;

/**
 * What to call a kind on screen IN A GIVEN LANGUAGE - the one derivation, used
 * by the guest storefront, the counter's ticket, the internal Menus tab and the
 * Kinds tab alike.
 *
 * The chain is the same one menu copy resolves through (`localizedText` in
 * `packages/db`): the requested locale, its base locale when it is regional
 * (`fr-CA` -> `fr`), then the authored English. A key with no record at all
 * reads as the key, which is the behaviour every surface had before the Kinds
 * tab existed - so an unregistered kind keeps reading exactly as it does today
 * rather than turning into a blank heading.
 */
export function productTypeLabelIn(
  type: string,
  locale: string,
  catalogue: ProductTypeCatalogue,
): string {
  const authored = catalogue[type];
  if (authored !== undefined) {
    const dash = locale.indexOf('-');
    const chain = dash === -1 ? [locale] : [locale, locale.slice(0, dash)];
    for (const candidate of chain) {
      const value = candidate === 'fr' ? authored.fr : candidate === 'en' ? authored.en : undefined;
      if (value !== undefined && value.trim() !== '') return value;
    }
    if (authored.en.trim() !== '') return authored.en;
  }
  return type;
}

/** One section of the vitrine: a kind, and the items carrying it. */
export interface ProductTypeSection<T> {
  readonly type: string;
  readonly items: readonly T[];
}

/**
 * Split a shelf into its sections, IN THE ORDER THE SHELF ITSELF IS IN.
 *
 * The section order is not a second decision to make and get wrong: a kind
 * appears where its first item appears. That is only the right answer once the
 * shelf has been put in kind order, which is what `sortByProductTypeOrder`
 * below is for and what both the guest route and the Menus tab do before
 * calling this. Items arrive already ranked - this preserves whatever order it
 * is handed and never sorts.
 */
export function productTypeSections<T extends { readonly kind: string }>(
  items: readonly T[],
): readonly ProductTypeSection<T>[] {
  const sections: { type: string; items: T[] }[] = [];
  const byType = new Map<string, { type: string; items: T[] }>();
  for (const item of items) {
    let section = byType.get(item.kind);
    if (section === undefined) {
      section = { type: item.kind, items: [] };
      byType.set(item.kind, section);
      sections.push(section);
    }
    section.items.push(item);
  }
  return sections;
}

/**
 * The shelf in KIND order, each kind's items left in the merchandising order
 * they arrived in (CAT-007, CAT-016).
 *
 * One sort, shared by the guest storefront and the internal Menus tab, because
 * the whole point of the Kinds tab's running order is that the operator arranges
 * the sections and the guest reads them in that sequence. A kind with no record
 * - one nobody has named yet - sorts after every kind that has one, in the order
 * its first item appears, so an unregistered section never jumps to the top of a
 * menu on the strength of having no position at all.
 *
 * Stable: `Array.prototype.sort` is stable in every runtime this ships to, and
 * that is what keeps `rank` deciding within a section.
 */
export function sortByProductTypeOrder<T extends { readonly kind: string }>(
  items: readonly T[],
  order: readonly string[],
): readonly T[] {
  const rank = new Map(order.map((key, index) => [key, index] as const));
  const unranked = new Map<string, number>();
  for (const item of items) {
    if (rank.has(item.kind) || unranked.has(item.kind)) continue;
    unranked.set(item.kind, order.length + unranked.size);
  }
  const positionOf = (kind: string): number => rank.get(kind) ?? unranked.get(kind) ?? order.length;
  return [...items].sort((a, b) => positionOf(a.kind) - positionOf(b.kind));
}
